import StoreCountry from 'GlobalStores/Cores/County/StoreCountry'
import { makeAutoObservable, runInAction } from 'mobx'
import SessionAuth from 'GlobalStores/User/SessionAuth'
import storeAccountConflict from 'GlobalStores/User/StoreAccountConflict'
import Australia from './Australia'
import Singapore from './Singapore'
import Newzealand from './Newzealand'
import Philippines from './Philippines'
import Taiwan from './Taiwan'
import Malaysia from './Malaysia'
import Morocco from './Morocco'
import Hongkong from './Hongkong'
import Japan from './Japan'
import Egypt from './Egypt'
import Turkiye from './Turkiye'
import Jordan from './Jordan'
import Southafrica from './Southafrica'
import Thailand from './Thailand'
import Kuwait from './Kuwait'
import SaudiArabia from './Saudiarabia'

const COUNTRY_REGISTRY = {
    AUS: Australia,
    SGP: Singapore,
    NZL: Newzealand,
    PHL: Philippines,
    TWN: Taiwan,
    MYS: Malaysia,
    MAR: Morocco,
    HKG: Hongkong,
    JPN: Japan,
    EGY: Egypt,
    TUR: Turkiye,
    JOR: Jordan,
    SAU: SaudiArabia,
    ZAF: Southafrica,
    THA: Thailand,
    KWT: Kuwait
}

class Checkout {
    instance = null

    constructor() {
        makeAutoObservable(this)
    }

    // ─── Instance resolution ────────────────────────────────────────────

    Instance() {
        if (!this.instance) {
            const CountryClass = COUNTRY_REGISTRY[StoreCountry.Country3()]
            if (CountryClass) {
                this.instance = new CountryClass()
            }
        }
        return this.instance
    }

    // ─── Delegation helpers ─────────────────────────────────────────────

    _getProperty(propertyName) {
        if (this.Instance()) {
            return this.Instance()[propertyName]
        }
    }

    _delegate(methodName, ...args) {
        if (this.Instance()) {
            return this.Instance()[methodName](...args)
        }
    }

    _delegateSafe(methodName, ...args) {
        if (this.Instance()) {
            try {
                return this.Instance()[methodName](...args)
            } catch (error) {
                return null
            }
        }
    }

    async _delegateAsync(methodName, ...args) {
        if (this.Instance()) {
            return await this.Instance()[methodName](...args)
        }
    }

    // Method 2 — safety net. Before any money-moving submit, confirm the shared
    // token still belongs to the account that owns THIS tab. If another tab
    // logged in and overrode the token, block the charge and surface the modal
    // instead of silently ordering for the wrong account.
    _assertTokenOwnership() {
        if (!SessionAuth.IsTokenOwnedByThisTab()) {
            storeAccountConflict.Trigger()
            const error = new Error('account_conflict')
            error.isAccountConflict = true
            throw error
        }
    }

    // ─── Property accessors ─────────────────────────────────────────────

    ArrangeCreateCheckoutForm() {
        return this._getProperty('ArrangeCreateCheckoutForm')
    }

    AutoAddressDisplayKeys() {
        return this._getProperty('autoAddressDisplayKeys')
    }

    ExcludedFieldsShippingIfCreateAccount() {
        return this._getProperty('ExcludedFieldsShippingIfCreateAccount')
    }

    ExcludedFieldsShipping() {
        return this._getProperty('ExcludedFieldsShipping')
    }

    CustomCreateAccountFormsLoginPage() {
        return this._getProperty('CustomCreateAccountFormsLoginPage')
    }

    CustomShippingForms() {
        return this._getProperty('CustomShippingForms')
    }

    CustomCreateAccountWithShippingForms() {
        return this._getProperty('CustomCreateAccountForms')
    }

    ExtendShippingForms() {
        return this._getProperty('ExtendShippingForms')
    }

    ExtendCreateAccountWithShippingForms() {
        return this._getProperty('ExtendCreateAccountForms')
    }

    PickupShippingForms() {
        return this._getProperty('PickupShippingForms')
    }

    // ─── Sync method delegations ────────────────────────────────────────

    GetPaymentStep() {
        return this._delegate('GetPaymentStep')
    }

    GetBreadcrumbOption() {
        return this._delegate('GetBreadcrumbOption')
    }

    GetLandingLayout() {
        return this._delegate('GetLandingLayout')
    }

    GetProductLayout() {
        return this._delegate('GetProductLayout')
    }

    FormToFormat(...args) {
        return this._delegate('FormToFormat', ...args)
    }

    GetFormat(...args) {
        return this._delegate('GetFormat', ...args)
    }

    GetUuid() {
        if (this.Instance()) {
            return this.Instance().GetUuid()
        }
        return ''
    }

    SetUuid(...args) {
        return this._delegate('SetUuid', ...args)
    }

    GetPaymentId() {
        return this._delegate('GetPaymentId')
    }

    GetBranchPickupDetail() {
        return this._delegate('GetBranchPickupDetail')
    }

    GetInvoiceIndividualForms() {
        return this._delegate('GetInvoiceIndividualForms')
    }

    GetInvoiceCorporateForms() {
        return this._delegate('GetInvoiceCorporateForms')
    }

    GetInvoiceValue() {
        return this._delegate('GetInvoiceValue')
    }

    GetTaxInvoiceValues() {
        return this._delegate('GetTaxInvoiceValues')
    }

    SetInvoiceValue() {
        return this._delegate('SetInvoiceValue')
    }

    // ─── Safe delegations (with try-catch) ──────────────────────────────

    GetShippingMethod() {
        return this._delegateSafe('GetShippingMethod')
    }

    GetOrderSummaryConfig() {
        return this._delegateSafe('GetOrderSummaryConfig')
    }

    GetBypassPage() {
        return this._delegateSafe('GetBypassPage')
    }

    GetNotShowReferralBar() {
        return this._delegateSafe('GetNotShowReferralBar')
    }

    GetPageConfig() {
        return this._delegateSafe('GetPageConfig')
    }

    SetPaymentMethod(...args) {
        return this._delegateSafe('SetPaymentMethod', ...args)
    }

    GetPaymentMethod() {
        return this._delegateSafe('GetPaymentMethod')
    }

    // ─── Async delegations ──────────────────────────────────────────────

    async CreateAccount(...args) {
        return this._delegateAsync('CreateAccount', ...args)
    }

    async SubmitPayment(...args) {
        this._assertTokenOwnership()
        return this._delegateAsync('SubmitPayment', ...args)
    }

    async SubmitBankwirePayment(...args) {
        this._assertTokenOwnership()
        return this._delegateAsync('SubmitBankwirePayment', ...args)
    }

    async SubmitCashOnDeliveryPayment(...args) {
        this._assertTokenOwnership()
        return this._delegateAsync('SubmitCashOnDeliveryPayment', ...args)
    }

    async SubmitPaymentWithApplePay(...args) {
        this._assertTokenOwnership()
        return this._delegateAsync('SubmitPaymentWithApplePay', ...args)
    }

    async GetPaymentChannel() {
        if (this.Instance()) {
            return await this.Instance().paymentChannel
        }
    }

    async ContinueToPayment() {
        return this._delegateAsync('ContinueToPayment')
    }

    // ─── Special methods ────────────────────────────────────────────────

    MapAutoAddressToForm(...args) {
        runInAction(() => {
            this.Instance().MapAutoAddressToForm(...args)
        })
    }

    CreditCardEncrypted = CreditCard => {
        let rearrangeExpDate = CreditCard.creditCardExpires.split('/')
        rearrangeExpDate = `${parseInt(rearrangeExpDate[1]) + 2000}-${rearrangeExpDate[0]}`

        return {
            type: 'AuthorizeAndCapture',
            method: 'CreditCard',
            amount: 'this.terms.total',
            methodDetails: {
                payer: CreditCard.payer,
                creditCardNumber: CreditCard.creditCardNumber,
                creditCardExpires: rearrangeExpDate,
                creditCardSecurityCode: CreditCard.creditCardSecurityCode
            },
            billToAddress: {
                country: StoreCountry.Country2()
            }
        }
    }
}

export default Checkout
